/**
 * Предметная модель лаборатории: семейства кривых и сцена из нескольких кривых.
 *
 * Математика кривой и её оформление разделены намеренно. `FunctionCurve` —
 * только коэффициенты; цвет, видимость и активность живут в `SceneCurve`.
 * Иначе «сбросить кривую» или «сравнить две параболы» начинают задевать
 * палитру, а тесты математики тянут за собой интерфейс.
 */

import { exact, type ExactNumber } from "./numbers";
import { DEFAULT_CAMERA, type PlotCamera } from "./viewport";

export type LineCurve = { kind: "line"; a: ExactNumber; b: ExactNumber };
/**
 * Общий вид: `y = ax² + bx + c`. Парабола заводится именно в нём — так она
 * записана в учебнике и в условии задачи, и ученик должен узнавать на экране
 * то, что ему дали на листке.
 */
export type ParabolaCurve = { kind: "parabola"; a: ExactNumber; b: ExactNumber; c: ExactNumber };
/**
 * Вид с вершиной: `y = a(x − h)² + k`. Отдельное семейство, а не второе
 * состояние параболы: в нём другие коэффициенты, другие ползунки и другой
 * смысл каждого числа. Попасть в него можно только преобразованием — тем
 * самым выделением полного квадрата, ради которого лаборатория и нужна.
 */
export type VertexCurve = { kind: "vertex"; a: ExactNumber; h: ExactNumber; k: ExactNumber };
export type ReciprocalCurve = { kind: "reciprocal"; a: ExactNumber; h: ExactNumber; k: ExactNumber };
/**
 * Три элементарные функции девятого класса — `|x|`, `√x`, `x³` — в одном
 * виде `y = a·f(x − h) + k`. Тот же вид, что у параболы с вершиной и у
 * гиперболы: сдвиги и множитель значат одно и то же на любой кривой, и именно
 * это переносится с одного графика на другой.
 */
export type AbsCurve = { kind: "abs"; a: ExactNumber; h: ExactNumber; k: ExactNumber };
export type SqrtCurve = { kind: "sqrt"; a: ExactNumber; h: ExactNumber; k: ExactNumber };
export type CubicCurve = { kind: "cubic"; a: ExactNumber; h: ExactNumber; k: ExactNumber };
/**
 * Окружность и эллипс — не функции, а уравнения с двумя переменными: одному
 * `x` отвечают два `y`. Поэтому в записи нет «y =», и пробник показывает
 * пару значений.
 *
 * В окружности редактируется радиус, а не правая часть: в условии стоит
 * `= 9`, а спрашивают радиус 3, и путаница между ними — та самая ошибка,
 * ради которой правая часть печатается вычисленной.
 */
export type CircleCurve = { kind: "circle"; h: ExactNumber; k: ExactNumber; r: ExactNumber };
/** Эллипс без поворота: `a` и `b` — полуоси по x и по y. */
export type EllipseCurve = {
  kind: "ellipse"; h: ExactNumber; k: ExactNumber; a: ExactNumber; b: ExactNumber;
};

export type FunctionCurve =
  | LineCurve | ParabolaCurve | VertexCurve | ReciprocalCurve
  | AbsCurve | SqrtCurve | CubicCurve | CircleCurve | EllipseCurve;
export type CurveFamily = FunctionCurve["kind"];
export type ParameterKey = "a" | "b" | "c" | "h" | "k" | "r";

/** Семейства, у которых одному `x` отвечает больше одного `y`. */
export function isRelation(family: CurveFamily): boolean {
  return family === "circle" || family === "ellipse";
}

/** Больше четырёх кривых на одной плоскости перестают сравниваться. */
export const MAX_CURVES = 4;

export type SceneCurve = {
  id: string;
  /**
   * Слот палитры, а не позиция в списке. Закреплён за кривой на всю её жизнь:
   * удаление соседней не перекрашивает оставшиеся — иначе ученик, сравнивший
   * «синюю с зелёной», после удаления третьей смотрит на другие цвета.
   */
  slot: number;
  visible: boolean;
  curve: FunctionCurve;
};

export type LabScene = {
  curves: SceneCurve[];
  activeId: string;
  camera: PlotCamera;
  tableOpen: boolean;
};

export const FAMILY_NAMES: Record<CurveFamily, string> = {
  line: "Прямая",
  parabola: "Парабола",
  vertex: "Парабола",
  reciprocal: "Гипербола",
  abs: "Модуль",
  sqrt: "Корень",
  cubic: "Кубическая парабола",
  circle: "Окружность",
  ellipse: "Эллипс",
};

export type ParameterMeta = {
  key: ParameterKey;
  /** Символ в формуле. */
  symbol: string;
  /** Доступное имя — для screen reader и подписи ползунка. */
  name: string;
  /**
   * Шаг ползунка по умолчанию. У множителя он мельче, чем у сдвига: `a`
   * меняет форму кривой, и разница между 1 и 1,1 видна, а сдвиг на 0,1
   * клетки не читается на глаз вовсе. Ученик меняет шаг рядом с ползунком.
   */
  step: number;
};

const PARAMETERS: Record<CurveFamily, ParameterMeta[]> = {
  line: [
    { key: "a", symbol: "a", name: "наклон", step: 0.1 },
    { key: "b", symbol: "b", name: "пересечение с осью y", step: 1 },
  ],
  parabola: [
    { key: "a", symbol: "a", name: "направление и ширина ветвей", step: 0.1 },
    { key: "b", symbol: "b", name: "коэффициент при x", step: 1 },
    { key: "c", symbol: "c", name: "пересечение с осью y", step: 1 },
  ],
  vertex: [
    { key: "a", symbol: "a", name: "направление и ширина ветвей", step: 0.1 },
    { key: "h", symbol: "h", name: "сдвиг по горизонтали", step: 1 },
    { key: "k", symbol: "k", name: "сдвиг по вертикали", step: 1 },
  ],
  reciprocal: [
    { key: "a", symbol: "a", name: "ориентация и масштаб ветвей", step: 0.1 },
    { key: "h", symbol: "h", name: "сдвиг по горизонтали", step: 1 },
    { key: "k", symbol: "k", name: "сдвиг по вертикали", step: 1 },
  ],
  abs: [
    { key: "a", symbol: "a", name: "направление и крутизна сторон", step: 0.1 },
    { key: "h", symbol: "h", name: "сдвиг по горизонтали", step: 1 },
    { key: "k", symbol: "k", name: "сдвиг по вертикали", step: 1 },
  ],
  sqrt: [
    { key: "a", symbol: "a", name: "направление и растяжение ветви", step: 0.1 },
    { key: "h", symbol: "h", name: "сдвиг по горизонтали", step: 1 },
    { key: "k", symbol: "k", name: "сдвиг по вертикали", step: 1 },
  ],
  cubic: [
    { key: "a", symbol: "a", name: "направление и крутизна", step: 0.1 },
    { key: "h", symbol: "h", name: "сдвиг по горизонтали", step: 1 },
    { key: "k", symbol: "k", name: "сдвиг по вертикали", step: 1 },
  ],
  circle: [
    { key: "h", symbol: "h", name: "абсцисса центра", step: 1 },
    { key: "k", symbol: "k", name: "ордината центра", step: 1 },
    { key: "r", symbol: "r", name: "радиус", step: 0.5 },
  ],
  ellipse: [
    { key: "h", symbol: "h", name: "абсцисса центра", step: 1 },
    { key: "k", symbol: "k", name: "ордината центра", step: 1 },
    { key: "a", symbol: "a", name: "полуось по x", step: 0.5 },
    { key: "b", symbol: "b", name: "полуось по y", step: 0.5 },
  ],
};

export function parametersOf(family: CurveFamily): ParameterMeta[] {
  return PARAMETERS[family];
}

/**
 * Базовые параметры семейства: `y = x`, `y = x²`, `y = 1/x`, `y = |x|`,
 * `y = √x`, `y = x³`, единичная окружность и эллипс с полуосями 2 и 1 —
 * круг с «овалом» по умолчанию не спутать.
 */
export function defaultCurve(family: CurveFamily): FunctionCurve {
  const one = exact(1);
  const zero = exact(0);
  switch (family) {
    case "line":
      return { kind: "line", a: one, b: zero };
    case "parabola":
      return { kind: "parabola", a: one, b: zero, c: zero };
    case "vertex":
    case "reciprocal":
    case "abs":
    case "sqrt":
    case "cubic":
      return { kind: family, a: one, h: zero, k: zero };
    case "circle":
      return { kind: "circle", h: zero, k: zero, r: one };
    case "ellipse":
      return { kind: "ellipse", h: zero, k: zero, a: exact(2), b: one };
  }
}

function hasParameter(family: CurveFamily, key: ParameterKey): boolean {
  return PARAMETERS[family].some((meta) => meta.key === key);
}

export function parameterValue(curve: FunctionCurve, key: ParameterKey): ExactNumber | null {
  if (!hasParameter(curve.kind, key)) return null;
  return (curve as unknown as Record<ParameterKey, ExactNumber>)[key];
}

export function setParameter(
  curve: FunctionCurve,
  key: ParameterKey,
  value: ExactNumber,
): FunctionCurve {
  if (!hasParameter(curve.kind, key)) return curve;
  return { ...curve, [key]: value } as FunctionCurve;
}

/** Замена кривой целиком — преобразование записи меняет и семейство. */
export function replaceCurve(scene: LabScene, id: string, curve: FunctionCurve): LabScene {
  return setActiveCurve(updateCurve(scene, id, (c) => ({ ...c, curve })), id);
}

/** Следующий свободный слот палитры, а не `curves.length`. */
function freeSlot(curves: SceneCurve[]): number {
  const taken = new Set(curves.map((c) => c.slot));
  for (let slot = 0; slot < MAX_CURVES; slot++) if (!taken.has(slot)) return slot;
  return MAX_CURVES - 1;
}

function freeId(curves: SceneCurve[]): string {
  let next = 1;
  for (const curve of curves) {
    const parsed = Number(curve.id.replace(/^curve-/, ""));
    if (Number.isFinite(parsed) && parsed >= next) next = parsed + 1;
  }
  return `curve-${next}`;
}

export function addCurve(scene: LabScene, family: CurveFamily): LabScene {
  if (scene.curves.length >= MAX_CURVES) return scene;
  const added: SceneCurve = {
    id: freeId(scene.curves),
    slot: freeSlot(scene.curves),
    visible: true,
    curve: defaultCurve(family),
  };
  return { ...scene, curves: [...scene.curves, added], activeId: added.id };
}

/**
 * Удаление кривой. Активная всегда существует, пока сцена непуста: иначе
 * панель коэффициентов остаётся без предмета и экран пустеет на ровном месте.
 */
export function removeCurve(scene: LabScene, id: string): LabScene {
  const curves = scene.curves.filter((c) => c.id !== id);
  if (curves.length === scene.curves.length) return scene;
  const activeId =
    scene.activeId === id ? (curves[0]?.id ?? "") : scene.activeId;
  return { ...scene, curves, activeId };
}

export function updateCurve(
  scene: LabScene,
  id: string,
  change: (curve: SceneCurve) => SceneCurve,
): LabScene {
  return { ...scene, curves: scene.curves.map((c) => (c.id === id ? change(c) : c)) };
}

export function setActiveCurve(scene: LabScene, id: string): LabScene {
  return scene.curves.some((c) => c.id === id) ? { ...scene, activeId: id } : scene;
}

export function toggleVisible(scene: LabScene, id: string): LabScene {
  return updateCurve(scene, id, (c) => ({ ...c, visible: !c.visible }));
}

/** Сброс трогает только свою кривую — соседние коэффициенты остаются. */
export function resetCurve(scene: LabScene, id: string): LabScene {
  return updateCurve(scene, id, (c) => ({ ...c, curve: defaultCurve(c.curve.kind) }));
}

export function activeCurve(scene: LabScene): SceneCurve | null {
  return scene.curves.find((c) => c.id === scene.activeId) ?? scene.curves[0] ?? null;
}

/** Первый вход: одна активная прямая `y = x` и камера у начала координат. */
export function newScene(): LabScene {
  return {
    curves: [{ id: "curve-1", slot: 0, visible: true, curve: defaultCurve("line") }],
    activeId: "curve-1",
    camera: DEFAULT_CAMERA,
    tableOpen: true,
  };
}
